import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    Stack,
    Box,
} from '@mui/material';

import CircularProgress from '../../../common/CircularProgress';
import { useMessages } from '../../../../hooks/useMessages';
import { dialogClose } from '../../../../store/actions/dialogActions';
import { Item as I } from '../../../../services/itemUtils';
import { FIELDS as F } from '../../../../services/consumerFields';
import * as DM from '../../../../services/dataModel';
import * as C from '../../../../config/marketConfig';
import FieldSection from '../FieldSection';
import { otpPhoneSetToken } from '../../../../store/actions/otpPhoneActions';
import { notifyError } from '../../../../store/actions/notificationActions';

const INITIAL_STATE = {
    editMode: true,
};

function getFieldsSend(L, market) {
    const items = [
        I.new(L.dialog_otp_new_phone_number, F.MOBILE_NUMBER)
            .editable()
            .required()
            .regexpValidator(
                C.getMarketValue(C.MARKET_REGEXP_MOBILE, market),
                L.consumerpage_error_phone
            )
            .build(),
    ];
    return { id: 1, items };
}

function getFieldsVerify(L, market) {
    const items = [
        I.new(L.dialog_otp_code, F.MOBILE_OTP_CODE)
            .editable()
            .required()
            .regexpValidator(
                C.getMarketValue(C.MARKET_REGEXP_OTP_CODE, market),
                L.dialog_otp_code_invalid
            )
            .build(),
    ];
    return { id: 1, items };
}

const OtpPhoneDialog = () => {
    const dispatch = useDispatch();
    const { L } = useMessages();
    const [model, setModel] = useState(INITIAL_STATE);

    const dialog = useSelector((state) => state.dialog);
    const { isOpen, isLoading, error, onConfirm } = dialog;

    const { user } = useSelector((state) => state.auth);
    const { market } = user || {};

    const { token } = useSelector((state) => state.otpPhone);
    const step1 = !token;

    const section = useMemo(() => {
        return step1 ? getFieldsSend(L, market) : getFieldsVerify(L, market);
    }, [L, market, step1]);

    // reset state on dialog open/close
    useEffect(() => {
        setModel({ ...INITIAL_STATE });
    }, [setModel, isOpen]);

    // init model from global state
    useEffect(() => {
        setModel((prev) => ({ ...prev, token }));
    }, [token]);

    // enable edit mode based on the loading state
    useEffect(() => {
        setModel((prev) => ({ ...prev, editMode: !isLoading }));
    }, [isLoading]);

    // handle API errors
    useEffect(() => {
        if (!isOpen || !error) return;
        const { info } = error;

        if (info === 'OTP_PHONE_NUMBER_IN_USE') {
            setModel((prev) =>
                DM.setFieldError(
                    prev,
                    F.MOBILE_NUMBER,
                    L.dialog_otp_new_phone_rejected
                )
            );
            return;
        }

        if (info === 'OTP_TOKEN_EXPIRED') {
            setModel((prev) =>
                DM.setFieldError(
                    prev,
                    F.MOBILE_OTP_CODE,
                    L.dialog_otp_code_expired
                )
            );
            return;
        }

        if (info === 'OTP_INVALID_CODE') {
            setModel((prev) =>
                DM.setFieldError(
                    prev,
                    F.MOBILE_OTP_CODE,
                    L.dialog_otp_code_rejected
                )
            );
            return;
        }

        // unknown error
        dispatch(dialogClose());
        dispatch(notifyError(L.err_error_unknown));
    }, [dispatch, setModel, L, isOpen, error]);

    const labels = useMemo(() => {
        return step1
            ? {
                  title: L.dialog_otp_phone_title,
                  subtitle: L.dialog_otp_phone_subtitle,
                  submit: L.dialog_otp_btn_send,
                  cancel: L.dialog_btn_cancel,
              }
            : {
                  title: L.dialog_otp_phone_verify_title,
                  subtitle: L.dialog_otp_phone_verify_subtitle,
                  submit: L.dialog_otp_btn_verify,
                  cancel: L.dialog_btn_cancel,
                  again: L.dialog_otp_btn_again,
              };
    }, [L, step1]);

    const canConfirm = useMemo(
        () =>
            section.items.every(
                (item) =>
                    DM.getCurrentValue(model, item.field) &&
                    DM.isValid(model, item.field)
            ),
        [section, model]
    );

    const handleChange = (itemValue) => {
        setModel((prev) => DM.setItemValue(prev, itemValue));
    };

    const onDialogConfirm = () => {
        onConfirm(model);
    };

    const onDialogAgain = () => {
        // reset local and global state
        setModel({ ...INITIAL_STATE });
        dispatch(otpPhoneSetToken(null));
    };

    const onDialogClose = () => {
        dispatch(dialogClose());
    };

    return (
        <Dialog
            aria-labelledby="consumer-block-dialog-title"
            aria-describedby="consumer-block-dialog-desc"
            open={isOpen}
            onClose={onDialogClose}
        >
            <DialogContent
                sx={{ mt: 1, minWidth: '500px', minHeight: '210px' }}
            >
                <Typography id="consumer-block-dialog-title" variant="h6">
                    {labels.title}
                </Typography>
                <Typography
                    id="consumer-block-dialog-desc"
                    variant="subtitle2"
                    sx={{ mt: 1, mb: 3 }}
                >
                    {labels.subtitle}
                </Typography>
                <FieldSection
                    section={section}
                    model={model}
                    onChange={handleChange}
                />
            </DialogContent>
            <DialogActions sx={{ mx: 2, my: 1 }}>
                {isLoading ? (
                    <Stack sx={{ width: '100%', alignItems: 'center' }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        {labels.again && (
                            <Button
                                variant="text"
                                onClick={onDialogAgain}
                                sx={{ minWidth: '5.6rem' }}
                            >
                                {labels.again}
                            </Button>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            onClick={onDialogClose}
                            sx={{ minWidth: '5.6rem' }}
                        >
                            {labels.cancel}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!canConfirm}
                            onClick={onDialogConfirm}
                            sx={{ minWidth: '5.6rem' }}
                        >
                            {labels.submit}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default OtpPhoneDialog;
